import { useForm } from "react-hook-form";
import { Stack, Button, Group, Text, Loader, Center } from "@mantine/core";
import {
  useUpdateRegistration,
  useGetRegistration,
} from "../../api/registrations";
import { useMemo, useEffect } from "react";
import BillingInformation from "../Registrations/Form/BillingInformation";
import resolver from "@congrs/resolver";
import { web_updateRegistrationSchema } from "@congrs/schemas";

export default function EditRegistrationForm({ registrationId, onClose }) {
  const updateMutation = useUpdateRegistration();

  const { data: registration, isPending: isLoadingRegistration } =
    useGetRegistration({
      registrationId,
    });

  const customFieldDefinitions = useMemo(() => {
    if (!registration?.customFields) return [];

    return registration.customFields.filter(
      (field) => field.appliedTo === "registrations"
    );
  }, [registration]);

  const schema = useMemo(() => {
    return web_updateRegistrationSchema(customFieldDefinitions);
  }, [customFieldDefinitions]);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      id: registrationId,
      type: "individual",
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      vatNumber: "",
      additionalFields: {},
    },
    mode: "onChange",
    resolver: resolver(schema, {
      allErrors: true,
      strictTypes: false,
    }),
  });

  useEffect(() => {
    if (!isLoadingRegistration && registration) {
      const additionalFields = registration.additionalFields || {};

      customFieldDefinitions.forEach((field) => {
        const fieldValue = additionalFields[field.key];

        if (field.type === "multiselect") {
          if (!fieldValue || !Array.isArray(fieldValue)) {
            additionalFields[field.key] = [];
          }
        } else if (field.type === "select") {
          if (fieldValue === null || fieldValue === undefined) {
            additionalFields[field.key] = "";
          }
        }
      });

      reset({
        id: registrationId,
        type: registration.type || "individual",
        firstName: registration.firstName || "",
        lastName: registration.lastName || "",
        email: registration.email || "",
        companyName: registration.companyName || "",
        vatNumber: registration.vatNumber || "",
        additionalFields: additionalFields,
      });
    }
  }, [
    isLoadingRegistration,
    registration,
    registrationId,
    reset,
    customFieldDefinitions,
  ]);

  const onSubmit = (data) => {
    const payload = {
      id: registrationId,
      email: data.email,
      additionalFields: data.additionalFields,
      firstName: data.firstName,
      lastName: data.lastName,
    };

    if (data.type === "company") {
      payload.companyName = data.companyName;
      if (data.vatNumber) {
        payload.vatNumber = data.vatNumber;
      }
    }

    updateMutation.mutate(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  if (isLoadingRegistration) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!registration) {
    return (
      <Center py="xl">
        <Text c="red">Failed to load registration data</Text>
      </Center>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack gap="lg">
        <BillingInformation
          control={control}
          registrationType={registration.type}
          registrationCustomFields={customFieldDefinitions}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={updateMutation.isPending}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
